const configure_action = [{ label: 'Configure', variant: 'primary', icon: 'bi-gear' }];
const chip_badges = {
  'Escalation path': { badge: 'Triggered', badge_class: 'bg-danger-subtle text-danger-emphasis' }
};

export function with_default(value, fallback) {
  return value || fallback;
}

export function item_section(title, items) {
  const section = { title, badge: 'Linked', badge_class: 'bg-light text-secondary border', kind: 'items', items };
  return section;
}

export function chips(title, items) {
  const badge = Reflect.get(chip_badges, title) || { badge: '', badge_class: '' };
  const section = { title, badge: badge.badge, badge_class: badge.badge_class, kind: 'chips', items };
  return section;
}

function stage_gate_count(template, fallback_count) {
  return `${with_default(template?.stage_count, fallback_count)} gates`;
}

export function timeline_section(template, fallback_count, items) {
  const timelines = {
    title: 'Stages & gates',
    badge: stage_gate_count(template, fallback_count),
    badge_class: 'bg-light text-secondary border',
    kind: 'timeline',
    items
  };
  return timelines;
}

function template_details(template, config) {
  return [
    { label: 'Type', value: config.type },
    { label: 'Owner', value: with_default(template?.owner, config.owner) },
    { label: 'Policy', value: with_default(template?.policy, config.policy) },
    { label: 'Control', value: with_default(template?.control, config.control) },
    { label: 'Stages', value: stage_gate_count(template, config.stage_count) }
  ];
}

export function build_template_profile(template, config) {
  const template_profile = {
    section: config.section,
    title: config.title,
    subtitle: config.subtitle,
    status: config.status,
    status_class: config.status_class,
    details_layout: 'list',
    details: template_details(template, config),
    sections: config.sections(template),
    footer_actions: configure_action
  };
  return template_profile;
}

function payment_exception_timeline() {
  const timeline = [{ title: 'Exception flagged', subtitle: 'Payment gate / AP clerk · SLA immediate', meta: 'Exception reason · Invoice', icon: 'bi-1-circle', icon_class: 'is-green', connected: true }, { title: '3-way match review', subtitle: 'Finance officer · SLA 1 day', meta: 'Purchase order · GRN · Invoice', icon: 'bi-2-circle', icon_class: 'is-green', connected: true }, { title: 'Deviation justification', subtitle: 'Requisitioner / SCM · SLA 2 days', meta: 'Motivation', icon: 'bi-3-circle', icon_class: 'is-green', connected: true }, { title: 'Finance review', subtitle: 'Finance Manager · SLA 1 day', meta: 'Budget confirmation', icon: 'bi-4-circle', icon_class: 'is-green', connected: true }, { title: 'Authority approval', subtitle: 'CIO / Accounting Officer · SLA Per DoA', meta: 'Approval log', icon: 'bi-5-circle', icon_class: 'is-slate', connected: true }, { title: 'Release or reject', subtitle: 'Finance · SLA 1 day', meta: 'Release / rejection record', icon: 'bi-6-circle', icon_class: 'is-slate', connected: false }];
  return timeline;
}

function supplier_revalidation_timeline() {
  const timeline = [{ title: 'Trigger', subtitle: 'System / SCM · SLA immediate', meta: 'Expiry alert', icon: 'bi-1-circle', icon_class: 'is-green', connected: true }, { title: 'Document refresh', subtitle: 'Supplier / SCM · SLA 5 days', meta: 'Tax · Banking · Insurance', icon: 'bi-2-circle', icon_class: 'is-green', connected: true }, { title: 'Re-verification', subtitle: 'Compliance · SLA 2 days', meta: 'Checks rerun', icon: 'bi-3-circle', icon_class: 'is-green', connected: true }, { title: 'Risk re-score', subtitle: 'Risk · SLA 1 day', meta: 'Updated score', icon: 'bi-4-circle', icon_class: 'is-slate', connected: true }, { title: 'Re-approval', subtitle: 'SCM Manager · SLA 2 days', meta: 'Approval log', icon: 'bi-5-circle', icon_class: 'is-slate', connected: true }, { title: 'Confirmed active', subtitle: 'Complyon · SLA 1 day', meta: 'Monitoring resumed', icon: 'bi-6-circle', icon_class: 'is-slate', connected: false }];
  return timeline;
}

const payment_template_variants = {
  payment_exception: {
    title: 'Payment exception workflow',
    subtitle: 'An invoice that fails the payment gate because a 3-way match breaks, supplier docs expire, or a budget line needs authority approval before release.',
    escalation_path: ['AP clerk', 'Finance Manager', 'CIO', 'Accounting Officer'],
    connects_to: ['Finance', 'Procurement', 'Suppliers', 'Delegation of authority', 'Evidence vault'],
    timeline: payment_exception_timeline
  },
  supplier_revalidation: {
    title: 'Supplier revalidation',
    subtitle: 'Refresh supplier evidence, re-score risk, and re-approve active vendors before expiry.',
    escalation_path: ['SCM officer', 'SCM Manager', 'CFO (payment hold)', 'Accounting Officer'],
    connects_to: ['Suppliers', 'Procurement', 'Finance', 'Risks', 'Evidence vault', 'Onboarding handoff'],
    timeline: supplier_revalidation_timeline
  }
};

export function build_payment_template(template, variant_key) {
  const variant = Reflect.get(payment_template_variants, variant_key);
  return build_template_profile(template, {
    section: 'TEMPLATE · COMPLYON',
    title: variant.title,
    subtitle: variant.subtitle,
    status: 'High risk',
    status_class: 'bg-danger-subtle text-danger-emphasis',
    type: 'Compliance template',
    owner: 'Finance',
    policy: 'POL-FIN',
    control: 'C-182',
    stage_count: 6,
    sections: (current_template) => [
      timeline_section(current_template, 6, variant.timeline()),
      chips('Escalation path', variant.escalation_path),
      chips('Connects to', variant.connects_to)
    ]
  });
}

function conflict_timeline() {
  const timeline = [{ title: 'Declaration', subtitle: 'Employee / director · SLA 1 day', meta: 'Disclosure form', icon: 'bi-1-circle', icon_class: 'is-green', connected: true }, { title: 'Review', subtitle: 'Compliance · SLA 2 days', meta: 'Related-party check', icon: 'bi-2-circle', icon_class: 'is-green', connected: true }, { title: 'Risk decision', subtitle: 'Ethics committee · SLA 3 days', meta: 'Decision log', icon: 'bi-3-circle', icon_class: 'is-green', connected: true }, { title: 'Mitigation', subtitle: 'Manager / Legal · SLA 5 days', meta: 'Mitigation actions', icon: 'bi-4-circle', icon_class: 'is-slate', connected: true }, { title: 'Monitoring', subtitle: 'Compliance · ongoing', meta: 'Annual review', icon: 'bi-5-circle', icon_class: 'is-slate', connected: false }];
  return timeline;
}

function training_timeline() {
  const timeline = [{ title: 'Non-compliance detected', subtitle: 'LMS / System · SLA immediate', meta: 'Overdue training report', icon: 'bi-1-circle', icon_class: 'is-green', connected: true }, { title: 'Reminder issued', subtitle: 'L&D · SLA 2 days', meta: 'Reminder log', icon: 'bi-2-circle', icon_class: 'is-green', connected: true }, { title: 'Manager escalation', subtitle: 'Line manager · SLA 3 days', meta: 'Manager acknowledgement', icon: 'bi-3-circle', icon_class: 'is-green', connected: true }, { title: 'Compliance gate', subtitle: 'Compliance · SLA 2 days', meta: 'Gate decision', icon: 'bi-4-circle', icon_class: 'is-green', connected: true }, { title: 'HR action', subtitle: 'HR Manager · SLA 5 days', meta: 'Action record', icon: 'bi-5-circle', icon_class: 'is-slate', connected: true }, { title: 'Resolved', subtitle: 'L&D · on completion', meta: 'Completion certificate', icon: 'bi-6-circle', icon_class: 'is-slate', connected: false }];
  return timeline;
}

const people_template_variants = {
  training_non_compliance: {
    title: 'Training non-compliance escalation',
    subtitle: 'When mandatory training is overdue, this workflow escalates from reminder to compliance gate and management action until completion.',
    escalation_path: ['Employee', 'Line manager', 'HR Manager', 'Compliance'],
    connects_to: ['Training', 'Employees', 'Policies', 'Audits', 'Evidence vault'],
    timeline: training_timeline
  },
  conflict_of_interest: {
    title: 'Conflict of interest review',
    subtitle: 'Declaration intake through review, mitigation and monitoring.',
    escalation_path: ['Employee', 'Manager', 'Compliance', 'Ethics committee'],
    connects_to: ['Conflicts', 'Policies', 'Audits', 'Evidence vault'],
    timeline: conflict_timeline
  }
};

export function build_people_template(template, variant_key) {
  const variant = Reflect.get(people_template_variants, variant_key);
  return build_template_profile(template, {
    section: 'TEMPLATE · COMPLYON',
    title: variant.title,
    subtitle: variant.subtitle,
    status: 'Medium risk',
    status_class: 'bg-warning-subtle text-warning-emphasis',
    type: 'Compliance template',
    owner: 'HR',
    policy: 'POL-08',
    control: 'C-203',
    stage_count: 6,
    sections: (current_template) => [
      timeline_section(current_template, 6, variant.timeline()),
      chips('Escalation path', variant.escalation_path),
      chips('Connects to', variant.connects_to)
    ]
  });
}
